
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { toast } from "@/hooks/use-toast";
import { UserProfile } from "@/types/gprofile";
import { GprofileService } from "@/services/gprofileService";
import { GprofileValidation } from "@/utils/gprofileValidation";

export const useGprofileData = (userId: string | undefined) => {
  const { user } = useAuth();
  const { checkSubscriptionLimit } = useSubscriptionStatus();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = async () => {
    const validationError = GprofileValidation.validateInputs(userId, user);
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }

    if (!userId || !user) return;

    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Fetching profile for userId:', userId);
      
      // Check if current user is blocked by the profile owner
      const isBlocked = await GprofileService.checkUserBlocked(userId, user.id);
      if (isBlocked) {
        console.log('❌ Current user is blocked by profile owner');
        setError('This profile is not available');
        setLoading(false);
        return;
      }
      
      // Fetch profile data
      const profileData = await GprofileService.fetchProfileData(userId);
      if (!profileData) {
        setError('Profile not found or not visible');
        setLoading(false);
        return;
      }

      // Get user status and verification
      const userData = await GprofileService.fetchUserStatus(userId);

      // Calculate online status
      const isOnline = GprofileService.calculateOnlineStatus(userData?.last_seen || null);

      // Calculate real distance
      const distance = await GprofileService.calculateDistance(user.id, userId);

      const finalProfile: UserProfile = {
        ...profileData,
        is_online: isOnline,
        last_seen: userData?.last_seen,
        distance,
        is_verified: userData?.is_verified || false,
        is_visible: profileData.is_visible,
        location_precision_meters: profileData.location_precision_meters,
        show_location_precision: profileData.show_location_precision
      };

      console.log('✅ Profile loaded successfully:', finalProfile);
      setProfile(finalProfile);
    } catch (error: any) {
      console.error('❌ Error fetching profile:', error);
      const errorMessage = `Failed to load profile: ${error.message || 'Unknown error'}`;
      setError(errorMessage);
      
      if (error.code !== 'PGRST116') {
        toast({
          title: "Error",
          description: "Failed to load profile. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔍 Gprofile data hook mounted with userId:', userId);
    console.log('🔍 Current user:', user?.id);
    
    if (userId && user) {
      fetchProfile();
      checkSubscriptionLimit(1);
    } else {
      console.log('❌ Missing userId or user authentication');
      setError('Missing user ID or authentication');
      setLoading(false);
    }
  }, [userId, user]);

  return {
    profile,
    loading,
    error,
    refetch: fetchProfile
  };
};
